import { varsFromServer } from '../client_side_injection_vars';
import { performComboSearch, performAlgoliaNearbySearch } from './location_search_api';
import {
  normalizeHits,
  getLocationUrl,
  HIT_TYPE_GEOLOCATED,
  HIT_TYPE_SUGGESTED,
  getHitType,
  getHitName
} from './location_search_util';
import { LocationSearchResults } from './location_search_results';
import { localizeLink } from '../util/locale';
import { debounce } from '../util/utils';
import { trackInteraction } from '../analytics';
import { t } from '../messages';
import createFocusTrap from 'focus-trap';

const MAX_HITS = 5;
const MOBILE_MAX_HITS = 3;

const NAV_LOCATIONS_OPEN_ID = 'nav-locations-open';
const NAV_LOCATIONS_CLOSE_ID = 'nav-locations-close';
const PAGE_NAV_SELECTOR = '.page-nav';

const MOBILE_LOCATION_SEARCH_ACTIVE = 'mobile-location-search--active';

let locationDropDownControl;
let locationSearchControl;
let locationDropDownClose;
let dropdownOpen = false;
// exported for test only. see location_search.js
export let locationFocusTrap;

let localDocument;

// memoize search results
let nearbyHits;

// @VisibleForTesting
export class LocationSearch {
  constructor(_window, selector, hideDropdown, isMobile = false) {
    this.window = _window;
    this.document = this.window.document;
    this.container = this.document.querySelector(selector);
    this.isMobile = isMobile;
    this.hideDropdown = hideDropdown;

    if (!this.window) {
      throw 'NO WINDOW';
    }

    if (!this.document) {
      throw 'NO DOCUMENT';
    }

    if (!this.container) {
      throw 'NO CONTAINER';
    }

    this.inputEl = this.container.querySelector('.location-search__input');
    this.inputEl.addEventListener('input', debounce(this.handleInput, 150));
    this.inputEl.addEventListener('keydown', this.handleKeyDown);

    if (isMobile) {
      this.inputEl.addEventListener('focus', this.handleMobileInputFocus);
      const mobileCancelButton = this.container.querySelector('.location-search__cancel');
      mobileCancelButton.addEventListener('click', this.handleMobileCancel);
    } else {
      // desktop
      this.resetIcon = this.document.querySelector('.location-search__reset-icon');
      this.resetIcon.hidden = true;
      this.resetIcon.addEventListener('click', this.handleReset);
    }

    this.resultsControl = new LocationSearchResults({
      container: this.container.querySelector('.location-search__results'),
      locale: varsFromServer.locale,
      handleSelect: this.handleSelect
    });

    const allLocationsLink = this.container.querySelector('.location-search__all-locations a');
    allLocationsLink.addEventListener('click', this.trackClickAllLocationsLink);

    this.updateNearbyHits();
    this.hasTrackEventBeenFiredOnType = false;
    this.trackNoTypingOneSec = debounce(trackInteraction, 1000);
    this.debouncedTrackInteraction = debounce(trackInteraction, 500);
  }

  getMaxHits() {
    return this.isMobile ? MOBILE_MAX_HITS : MAX_HITS;
  }

  async updateNearbyHits() {
    if (!nearbyHits) {
      nearbyHits = await searchNearbyHits();
    }
    const hits = this.isMobile ? nearbyHits.slice(0, this.getMaxHits()) : nearbyHits;
    this.resultsControl.updateResults({ hits });
  }

  reset(focus = true) {
    this.inputEl.value = '';
    if (focus && this.document.activeElement !== this.inputEl) {
      this.inputEl.focus();
    }
    this.resultsControl.reset();
    this.updateNearbyHits();

    if (!this.isMobile) {
      this.resetIcon.hidden = true;
    }
  }

  async getNormalizedHits({ query, locale }) {
    if (!query) {
      return { hits: [] };
    }

    const { exactHits, geolocatedHits, address, canceled } = await performComboSearch({
      query,
      locale,
      validateLatestQuery: this.validateLatestQuery
    });

    if (canceled) {
      return { canceled: true };
    }

    const hits = normalizeHits({ exactHits, geolocatedHits }).slice(0, this.getMaxHits());
    return { hits, address };
  }

  validateLatestQuery = query => {
    return query === this.latestQuery;
  };

  // event handlers

  handleReset = () => {
    this.reset();
  };

  handleInput = async event => {
    const query = event.target.value.trim();
    this.latestQuery = query;
    const { locale } = varsFromServer;

    if (query) {
      this.trackTyping(query);

      if (!this.isMobile) {
        this.resetIcon.hidden = false;
      }

      try {
        const { hits, address, canceled } = await this.getNormalizedHits({ query, locale });
        if (!canceled) {
          this.resultsControl.updateResults({ query, hits, address });
        }
      } catch (error) {
        console.log(error); // eslint-disable-line no-console
        this.resultsControl.updateResults({ error });
      }
    } else {
      this.reset(false);
    }
  };

  trackTyping(query) {
    if (!query) {
      return;
    }

    if (!this.hasTrackEventBeenFiredOnType) {
      this.hasTrackEventBeenFiredOnType = true;
      trackInteraction({
        label: 'Nav - Search Form - Start typing',
        query
      });
    }

    this.trackNoTypingOneSec({
      label: 'Search - No Typing: 1s',
      query
    });
  }

  handleMobileInputFocus = () => {
    this.container.classList.add(MOBILE_LOCATION_SEARCH_ACTIVE);
    this.reset();
    this.inputEl.placeholder = t('locationSearch.mobileActiveInputPlaceHolder');
  };

  handleMobileCancel = () => {
    this.container.classList.remove(MOBILE_LOCATION_SEARCH_ACTIVE);
    this.inputEl.value = '';
    this.inputEl.placeholder = t('locationSearch.mobileInputPlaceHolder');
    trackInteraction({
      label: 'Nav - Search Form - Click Cancel'
    });
  };

  handleSelect = selection => {
    this.trackSelection(selection);
    this.hasTrackEventBeenFiredOnType = false;

    const url = getLocationUrl({ selection });
    if (url) {
      // navigate
      return this.window.location.assign(localizeLink({ to: url, locale: varsFromServer.locale }));
    }
  };

  trackSelection(selection) {
    trackInteraction({
      label: 'Nav - Search Form - Select',
      query: this.latestQuery,
      locale: varsFromServer.locale,
      ...(selection && {
        selectionId: selection.id,
        selectionName: getHitName(selection, false),
        selectionNameLocalized: getHitName(selection, false, varsFromServer.locale),
        selectionHitType: getHitType(selection)
      })
    });
  }

  trackClickAllLocationsLink() {
    trackInteraction({
      label: 'Nav - Search Form - Select',
      locale: varsFromServer.locale,
      selection_name: 'All locations'
    });
    return true;
  }

  handleKeyDown = event => {
    const { key, keyCode } = event;

    if (key === 'ArrowUp' || keyCode === 38) {
      this.handleArrowUp(event);
    } else if (key === 'ArrowDown' || keyCode === 40) {
      this.handleArrowDown(event);
    } else if (key === 'Enter' || keyCode === 13) {
      this.handleEnter(event);
    } else if (key === 'Backspace' || keyCode === 8) {
      this.handleBackspace(event);
    } else if (key === 'Escape' || keyCode === 27) {
      this.handleEscape(event);
    }
  };

  handleArrowUp = event => {
    event.preventDefault();
    this.resultsControl.focusPrev();
  };

  handleArrowDown = event => {
    event.preventDefault();
    this.resultsControl.focusNext();
  };

  handleEnter = event => {
    event.preventDefault();
    this.resultsControl.select();
  };

  handleEscape = () => {
    this.reset();
    if (!this.isMobile) {
      this.hideDropdown();
    }
  };

  handleBackspace = () => {
    this.debouncedTrackInteraction({
      label: 'Search - Hit Backspace',
      query: this.latestQuery
    });
  };
} // end LocationSearch

// helpers

function hideDropdown() {
  dropdownOpen = true;
  toggleDropdown();
}

// VisibleForTesting
export function toggleDropdown() {
  const nav = getNav();
  if (!dropdownOpen) {
    nav.classList.add('location-search-dropdown-open');
    localDocument.body.classList.add('js-lock-scroll');
    locationSearchControl && locationSearchControl.reset();
    trackInteraction({
      label: 'Nav Location Dropdown',
      is_open: true
    });
  } else {
    localDocument.body.classList.remove('js-lock-scroll');
    nav.classList.remove('location-search-dropdown-open');
    locationDropDownControl.blur();
  }

  // a11y stuff
  toggleLocationDropdownAriaHidden(dropdownOpen);
  toggleTabIndex(
    ['#nav-locations-close', '#location-search-input', '#location-search-overlay .location-search__all-locations a'],
    dropdownOpen ? -1 : 0
  );
  toggleFocusTrap(dropdownOpen);
  dropdownOpen = !dropdownOpen;
}

function toggleFocusTrap(active) {
  active ? locationFocusTrap.deactivate() : locationFocusTrap.activate();
}

function toggleLocationDropdownAriaHidden(hidden) {
  const dropdown = localDocument.getElementById('location-search-overlay');
  if (dropdown) {
    dropdown.setAttribute('aria-hidden', hidden ? 'true' : 'false');
  }
}

function toggleTabIndex(selectors, tabIndex) {
  for (let selector of selectors) {
    const el = localDocument.querySelector(selector);
    if (el) {
      el.tabIndex = tabIndex;
    }
  }
}

function getNav() {
  return localDocument.querySelector(PAGE_NAV_SELECTOR);
}

// for testing
export function clearNearbyHits() {
  nearbyHits = null;
}

async function searchNearbyHits() {
  const {
    results: [{ hits }]
  } = await performAlgoliaNearbySearch(MAX_HITS);

  return hits.map((hit, index) => ({
    ...hit,
    hitType: index === 0 ? HIT_TYPE_GEOLOCATED : HIT_TYPE_SUGGESTED
  }));
}

export async function initLocationSearch(_window) {
  localDocument = _window.document;

  try {
    nearbyHits = await searchNearbyHits();
  } catch (err) {
    // ignore
  }

  locationSearchControl = new LocationSearch(_window, '.desktop-location-search', hideDropdown);
  new LocationSearch(_window, '.mobile-location-search', hideDropdown, true);

  locationDropDownControl = localDocument.getElementById(NAV_LOCATIONS_OPEN_ID);
  locationDropDownControl.addEventListener('click', toggleDropdown);

  locationDropDownClose = localDocument.getElementById(NAV_LOCATIONS_CLOSE_ID);
  locationDropDownClose.addEventListener('click', toggleDropdown);
  locationFocusTrap = createFocusTrap('#location-search-overlay', { clickOutsideDeactivates: true });
}
